import { PhoneFilled, PhoneOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Radio,
  Select,
  Slider,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
  FilterConfig,
  FilterMode,
  PhoneCallEvent,
  profileToConfig,
  usePhoneCall,
} from "./usePhoneCall";

const { Title, Text, Paragraph } = Typography;

// ── Layout ───────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100dvh;
  width: 100%;
  background: #0a0e14;
  color: #d1d5db;
  padding: 24px;
  display: flex;
  justify-content: center;
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1100px;
  display: grid;
  grid-template-columns: 380px 1fr;
  grid-gap: 20px;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const PanelCard = styled(Card)`
  background: #111722;
  border-color: #1f2937;
  color: #e5e7eb;
  .ant-card-head {
    color: #f3f4f6;
    border-color: #1f2937;
  }
  .ant-card-body {
    color: #d1d5db;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0;
`;

const Label = styled(Text)`
  color: #9ca3af !important;
  font-size: 13px;
`;

const CallButton = styled(Button)<{ $active: boolean }>`
  width: 100%;
  height: 56px;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${(p) => (p.$active ? "#dc2626" : "#16a34a")};
  border-color: ${(p) => (p.$active ? "#b91c1c" : "#15803d")};
  color: #fff;
  &:hover,
  &:focus {
    background: ${(p) => (p.$active ? "#ef4444" : "#22c55e")} !important;
    border-color: ${(p) => (p.$active ? "#dc2626" : "#16a34a")} !important;
    color: #fff !important;
  }
`;

const MeterTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #1f2937;
  border-radius: 4px;
  overflow: hidden;
`;

const MeterFill = styled.div<{ $level: number }>`
  width: ${(p) => Math.min(100, Math.max(0, p.$level * 100))}%;
  height: 100%;
  background: linear-gradient(90deg, #22c55e 0%, #eab308 70%, #ef4444 100%);
  transition: width 80ms linear;
`;

const TranscriptPane = styled.div`
  height: 280px;
  overflow-y: auto;
  background: #0b1220;
  border: 1px solid #1f2937;
  border-radius: 6px;
  padding: 10px;
  font-size: 13px;
`;

const TranscriptLine = styled.div<{ $bot: boolean }>`
  margin-bottom: 6px;
  color: ${(p) => (p.$bot ? "#93c5fd" : "#fcd34d")};
  &::before {
    content: "${(p) => (p.$bot ? "🤖 " : "🗣️ ")}";
  }
`;

const EventPane = styled.div`
  height: 200px;
  overflow-y: auto;
  background: #0b1220;
  border: 1px solid #1f2937;
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
`;

const EventLine = styled.div<{ $level: PhoneCallEvent["level"] }>`
  color: ${(p) =>
    p.$level === "error"
      ? "#fca5a5"
      : p.$level === "warn"
      ? "#fde68a"
      : "#9ca3af"};
`;

// ── Constants ────────────────────────────────────────────────────────

const USE_CASES = [
  { value: "ha_scenario", label: "HA scenario (Vietnamese, post-installation)" },
  { value: "connection_scenario", label: "Connection scenario" },
  { value: "technical_issue_scenario", label: "Technical issue scenario" },
  { value: "wifi_survey_scenario", label: "WiFi survey scenario" },
];

const LANGUAGES = [
  { value: "vi", label: "Tiếng Việt" },
  { value: "km", label: "ភាសាខ្មែរ" },
  { value: "en", label: "English" },
];

// ── Component ────────────────────────────────────────────────────────

const PhoneSimulator = () => {
  const [language, setLanguage] = useState("vi");
  const [useCase, setUseCase] = useState("ha_scenario");
  const [mode, setMode] = useState<FilterMode>("full");
  const [config, setConfig] = useState<FilterConfig>(() => profileToConfig("full"));
  const [inputSampleRate, setInputSampleRate] = useState(8000);
  const [outputSampleRate, setOutputSampleRate] = useState(16000);
  const [events, setEvents] = useState<PhoneCallEvent[]>([]);
  const eventScrollRef = useRef<HTMLDivElement | null>(null);
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);

  // Switching the profile preset updates the underlying config; flipping
  // any individual switch transitions to "custom" so the user knows
  // their picks are no longer aligned with a named preset.
  useEffect(() => {
    setConfig(profileToConfig(mode));
  }, [mode]);

  const updateField = useCallback(
    <K extends keyof FilterConfig>(key: K, value: FilterConfig[K]) => {
      setConfig((c) => ({ ...c, [key]: value }));
      setMode("custom");
    },
    []
  );

  const handleEvent = useCallback((ev: PhoneCallEvent) => {
    setEvents((prev) => [...prev.slice(-300), ev]);
  }, []);

  const phone = usePhoneCall({
    language,
    useCase,
    filterConfig: config,
    inputSampleRate,
    outputSampleRate,
    onEvent: handleEvent,
  });

  // Auto-scroll transcript + event log to bottom on update
  useEffect(() => {
    eventScrollRef.current?.scrollTo({ top: eventScrollRef.current.scrollHeight });
  }, [events]);
  useEffect(() => {
    transcriptScrollRef.current?.scrollTo({
      top: transcriptScrollRef.current.scrollHeight,
    });
  }, [phone.transcripts]);

  const onCallToggle = useCallback(async () => {
    if (phone.isConnected) {
      phone.disconnect();
    } else {
      try {
        await phone.connect();
      } catch (e) {
        // error already emitted by the hook
      }
    }
  }, [phone]);

  const lockedDuringCall = phone.isConnected;

  const profileSummary = useMemo(() => {
    const parts: string[] = [];
    if (config.bandpass) parts.push("Bandpass 300–3400 Hz");
    if (config.ulaw) parts.push("G.711 µ-law");
    if (config.noise) parts.push(`Noise (SNR ${config.noiseSnrDb} dB)`);
    if (parts.length === 0) return "No phone-like processing — clean PCM";
    const sides = [
      config.applyOnInput ? "input" : null,
      config.applyOnOutput ? "output" : null,
    ]
      .filter(Boolean)
      .join(" + ");
    return `${parts.join(", ")} • ${sides || "no side"}`;
  }, [config]);

  return (
    <Page>
      <Inner>
        {/* ── Settings panel ── */}
        <PanelCard title="Phone simulator">
          <Paragraph style={{ color: "#9ca3af", marginBottom: 16 }}>
            Streams your mic to the bot through a telephony-like processing
            chain (8 kHz, 300–3400 Hz bandpass, optional G.711 µ-law and
            line noise). Receives the bot's reply through the same chain so
            playback also sounds like a phone call. Settings are captured
            when you press Call — to change them, hang up first.
          </Paragraph>

          <Row>
            <Label>Use case</Label>
            <Select
              value={useCase}
              onChange={setUseCase}
              disabled={lockedDuringCall}
              style={{ width: 220 }}
              options={USE_CASES}
            />
          </Row>
          <Row>
            <Label>Language</Label>
            <Select
              value={language}
              onChange={setLanguage}
              disabled={lockedDuringCall}
              style={{ width: 220 }}
              options={LANGUAGES}
            />
          </Row>
          <Row>
            <Label>Input rate (mic → server)</Label>
            <Select
              value={inputSampleRate}
              onChange={setInputSampleRate}
              disabled={lockedDuringCall}
              style={{ width: 220 }}
              options={[
                { value: 8000, label: "8 kHz (telephony narrowband)" },
                { value: 16000, label: "16 kHz (wideband)" },
              ]}
            />
          </Row>
          <Row>
            <Label>Output rate (server → speaker)</Label>
            <Select
              value={outputSampleRate}
              onChange={setOutputSampleRate}
              disabled={lockedDuringCall}
              style={{ width: 220 }}
              options={[
                { value: 16000, label: "16 kHz (recommended — matches cache)" },
                { value: 8000, label: "8 kHz (may play 2× off if rate mismatched)" },
              ]}
            />
          </Row>

          <Divider style={{ borderColor: "#1f2937", margin: "14px 0" }} />

          <Row>
            <Label>Profile</Label>
            <Radio.Group
              value={mode}
              disabled={lockedDuringCall}
              onChange={(e) => setMode(e.target.value as FilterMode)}
              optionType="button"
              buttonStyle="solid"
              size="small"
              options={[
                { label: "Off", value: "off" },
                { label: "Light", value: "light" },
                { label: "Full", value: "full" },
                { label: "Custom", value: "custom" },
              ]}
            />
          </Row>

          <Row>
            <Label>Bandpass 300–3400 Hz</Label>
            <Switch
              checked={config.bandpass}
              disabled={lockedDuringCall}
              onChange={(v) => updateField("bandpass", v)}
            />
          </Row>
          <Row>
            <Label>G.711 µ-law roundtrip</Label>
            <Switch
              checked={config.ulaw}
              disabled={lockedDuringCall}
              onChange={(v) => updateField("ulaw", v)}
            />
          </Row>
          <Row>
            <Label>Line noise (live during a call)</Label>
            <Switch
              checked={config.noise}
              onChange={(v) => updateField("noise", v)}
            />
          </Row>
          {config.noise && (
            <Row>
              <Label>Noise SNR (dB) — higher = quieter</Label>
              <Slider
                min={10}
                max={45}
                value={config.noiseSnrDb}
                onChange={(v) => updateField("noiseSnrDb", v)}
                style={{ width: 200 }}
              />
            </Row>
          )}

          <Divider style={{ borderColor: "#1f2937", margin: "14px 0" }} />

          <Row>
            <Label>Apply on input (mic → server)</Label>
            <Switch
              checked={config.applyOnInput}
              disabled={lockedDuringCall}
              onChange={(v) => updateField("applyOnInput", v)}
            />
          </Row>
          <Row>
            <Label>Apply on output (server → speaker)</Label>
            <Switch
              checked={config.applyOnOutput}
              disabled={lockedDuringCall}
              onChange={(v) => updateField("applyOnOutput", v)}
            />
          </Row>

          <Paragraph
            style={{ color: "#6b7280", fontSize: 12, marginTop: 12, marginBottom: 0 }}
          >
            {profileSummary}
          </Paragraph>
        </PanelCard>

        {/* ── Right column ── */}
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <PanelCard
            title={
              <Space>
                <span>Call</span>
                {phone.isConnected ? (
                  <Tag color="green">connected</Tag>
                ) : (
                  <Tag color="default">idle</Tag>
                )}
                {phone.clientId && (
                  <Tag color="blue">client_id={phone.clientId}</Tag>
                )}
              </Space>
            }
          >
            <Row>
              <Label>Mic level</Label>
              <div style={{ flex: 1, marginLeft: 12 }}>
                <MeterTrack>
                  <MeterFill $level={phone.micLevel} />
                </MeterTrack>
              </div>
            </Row>

            <CallButton
              type="primary"
              $active={phone.isConnected}
              icon={phone.isConnected ? <PhoneFilled /> : <PhoneOutlined />}
              onClick={onCallToggle}
            >
              {phone.isConnected ? "Hang up" : "Call"}
            </CallButton>
          </PanelCard>

          <PanelCard title="Transcript">
            <TranscriptPane ref={transcriptScrollRef}>
              {phone.transcripts.length === 0 ? (
                <Text style={{ color: "#6b7280" }}>
                  Waiting for bot to speak…
                </Text>
              ) : (
                phone.transcripts.map((t, i) => (
                  <TranscriptLine key={i} $bot={t.speaker === "bot"}>
                    {t.text}
                  </TranscriptLine>
                ))
              )}
            </TranscriptPane>
          </PanelCard>

          <PanelCard title="Events">
            <EventPane ref={eventScrollRef}>
              {events.length === 0 ? (
                <Text style={{ color: "#6b7280" }}>No events yet.</Text>
              ) : (
                events.map((ev, i) => (
                  <EventLine key={i} $level={ev.level}>
                    {new Date(ev.ts).toISOString().slice(11, 19)}{" "}
                    [{ev.level}] {ev.text}
                  </EventLine>
                ))
              )}
            </EventPane>
          </PanelCard>
        </Space>
      </Inner>
    </Page>
  );
};

export default PhoneSimulator;
